import { Button, PasswordInput } from '@mantine/core'
import { useForm } from '@mantine/form'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMedplum } from '@medplum/react'
import { showNotification } from '@mantine/notifications'
import { useAppFlags } from '@/hooks/useAppFlags'
import { useClient } from '@/hooks/useClient'
import { passwordPolicyRequirements, validatePasswordPolicy } from '@/utils/passwordPolicy'
import { AuthCardLayout } from './components/AuthCardLayout'
import {
  AUTH_CLASSES,
  AUTH_LOGIN_PASSWORD_INPUT_STYLES,
  AUTH_LOGIN_PRIMARY_BUTTON_STYLES,
} from './authTheme'

type FormProps = {
  password: string
  confirmPassword: string
}

export function SetPassword() {
  const navigate = useNavigate()
  const { id, secret } = useParams()
  const medplum = useMedplum()
  const client = useClient()
  const { authV2Provider } = useAppFlags<{ authV2Provider?: boolean }>()

  const form = useForm({
    initialValues: {
      password: '',
      confirmPassword: '',
    },
    validate: {
      password: validatePasswordPolicy,
      confirmPassword: (value, values) =>
        value !== values.password ? 'Passwords do not match' : null,
    },
  })

  const handleSubmit = async (formData: FormProps) => {
    try {
      if (authV2Provider) {
        await client.authSetPassword({
          id: id as string,
          secret: secret as string,
          password: formData.password,
        })
      } else {
        await medplum.post('auth/setpassword', {
          id,
          secret,
          password: formData.password,
        })
      }
      showNotification({
        color: 'green',
        message: 'Password set successfully',
      })
      void navigate('/login')
    } catch (err) {
      showNotification({
        color: 'red',
        message: err instanceof Error ? err.message : 'Something went wrong',
      })
    }
  }

  return (
    <AuthCardLayout
      title="Set your password"
      description="Create a new password to access your beHuman account."
    >
      <form className="flex flex-col gap-4" onSubmit={form.onSubmit(handleSubmit)}>
        <div>
          <PasswordInput
            label="New password"
            placeholder="••••••••"
            autoComplete="new-password"
            required
            styles={AUTH_LOGIN_PASSWORD_INPUT_STYLES}
            // Sub-16px inputs make iOS Safari auto-zoom the page on focus.
            classNames={{
              input: AUTH_CLASSES.passwordFocus,
              innerInput: 'max-md:!text-[16px]',
            }}
            {...form.getInputProps('password')}
          />
          <ul className="mt-2.5 grid list-none grid-cols-2 gap-x-3 gap-y-0.5 p-0 max-[480px]:grid-cols-1">
            {passwordPolicyRequirements.map((requirement) => (
              <li
                key={requirement}
                className={`flex items-start gap-1.5 text-[11.5px] leading-[1.5] ${AUTH_CLASSES.secondaryText}`}
              >
                <span
                  className={`mt-[6px] block h-1 w-1 shrink-0 rounded-full ${AUTH_CLASSES.accentDot}`}
                />
                {requirement}
              </li>
            ))}
          </ul>
        </div>

        <PasswordInput
          label="Confirm new password"
          placeholder="••••••••"
          autoComplete="new-password"
          required
          styles={AUTH_LOGIN_PASSWORD_INPUT_STYLES}
          classNames={{
            input: AUTH_CLASSES.passwordFocus,
            innerInput: 'max-md:!text-[16px]',
          }}
          {...form.getInputProps('confirmPassword')}
        />

        <Button
          type="submit"
          loading={form.submitting}
          fullWidth
          size="md"
          styles={AUTH_LOGIN_PRIMARY_BUTTON_STYLES}
          classNames={{ root: AUTH_CLASSES.primaryButtonState }}
        >
          Set password
        </Button>

        <Link
          to="/login"
          className={`mt-2 self-center text-[14px] font-medium ${AUTH_CLASSES.accentLink}`}
        >
          Back to login
        </Link>
      </form>
    </AuthCardLayout>
  )
}
